/**
 * @typedef {Object} UserHistoryObservedEmergencyModelParams
 * @property {boolean} chat_opened - True once the user has opened the chat dialog.
 * @property {number} creation_timestamp - When the emergency was created (epoch milliseconds).
 * @property {string} creator_auth_id - Auth ID of the emergency creator.
 * @property {string} creator_first_name - First name of the creator.
 * @property {string} creator_last_name - Last name of the creator.
 * @property {string} incident - Incident type copied from the emergency.
 * @property {string} org_code - Organization code.
 * @property {string} group_name - Group name.
 * @property {number|null} [resolved_timestamp] - Timestamp of resolution, or null if unresolved.
 */

/**
 * Lightweight representation of an emergency that a user created or was part of.
 */
export class UserHistoryObservedEmergencyModel {
  /**
   * @param {UserHistoryObservedEmergencyModelParams} params
   */
  constructor({
    chat_opened,
    creation_timestamp,
    creator_auth_id,
    creator_first_name,
    creator_last_name,
    incident,
    org_code,
    group_name,
    resolved_timestamp = null,
  }) {
    /** @type {boolean} */
    this.chat_opened = chat_opened;

    /** @type {number} */
    this.creation_timestamp = creation_timestamp;

    /** @type {string} */
    this.creator_auth_id = creator_auth_id;

    /** @type {string} */
    this.creator_first_name = creator_first_name;

    /** @type {string} */
    this.creator_last_name = creator_last_name;

    /** @type {string} */
    this.incident = incident;

    /** @type {string} */
    this.org_code = org_code;

    /** @type {string} */
    this.group_name = group_name;

    /** @type {number|null} */
    this.resolved_timestamp = resolved_timestamp;
  }

  /**
   * Returns whether the emergency has been resolved.
   * @returns {boolean}
   */
  isResolved() {
    return this.resolved_timestamp != null;
  }

  /**
   * Returns whether the emergency is still active (not resolved and created within the last 24 hours).
   * @returns {boolean}
   */
  isActiveEmergency() {
    const timestamp24HoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    const isResolved =
      this.resolved_timestamp !== null && this.resolved_timestamp !== undefined;
    return !isResolved && this.creation_timestamp >= timestamp24HoursAgo;
  }
}
